import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import PaymentVoucher from '../components/PaymentVoucher'; // Import the new component
import moment from 'moment';
import { Printer, Edit, ArrowLeft, Camera, Trash2, CheckCircle, Clock, Car, User, Calendar, DollarSign, AlertCircle, MapPin, Gauge } from 'lucide-react';
import ReservationPrint from '../components/printpage/ReservationPrint2';
import FacturePrint from '../components/printpage/FacturePrint';
import { createRoot } from 'react-dom/client';

const API_BASE_URL = 'https://krili-loto.com';

const ReservationDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, i18n: i18nInstance } = useTranslation();
    const isRtl = i18nInstance.language === 'ar';
    const [reservation, setReservation] = useState(null);
    const [client, setClient] = useState(null);
    const [vehicle, setVehicle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [photos, setPhotos] = useState([]);
    const [newPhotos, setNewPhotos] = useState([]);
    const [showVoucher, setShowVoucher] = useState(false); // State for showing voucher

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [reservationRes, photosRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/reservations/details/${id}`),
                    axios.get(`${API_BASE_URL}/api/reservations/${id}/photos`)
                ]);

                const reservationData = reservationRes.data;
                setReservation(reservationData);
                setPhotos(photosRes.data);

                if (reservationData.client_id) {
                    const clientRes = await axios.get(`${API_BASE_URL}/api/clients/${reservationData.client_id}`);
                    setClient(clientRes.data);
                }

                if (reservationData.voiture_id) {
                    const vehicleRes = await axios.get(`${API_BASE_URL}/api/voitures/${reservationData.voiture_id}`);
                    setVehicle(vehicleRes.data);
                }

                setLoading(false);
            } catch (err) {
                setError(t('failed_load_details'));
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);


    const handlePrintVoucher = () => {
        const printWindow = window.open('', '_blank');

        // Write basic HTML structure first
        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
            <head>
                <title>Payment Voucher #${reservation.id}</title>
                <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
                <style>
                    @media print {
                        body { -webkit-print-color-adjust: exact; }
                        .no-print { display: none !important; }
                        body, html { margin: 0; padding: 0; }
                    }
                    body { padding: 20px; }
                </style>
            </head>
            <body class="bg-white">
                <div id="voucher-root"></div>
            </body>
        </html>
    `);

        // Close the document to finish writing
        printWindow.document.close();

        // Wait for the document to be fully loaded before rendering React
        const interval = setInterval(() => {
            if (printWindow.document.readyState === 'complete') {
                clearInterval(interval);

                const voucherRoot = printWindow.document.getElementById('voucher-root');

                // Create root and render the component
                const root = createRoot(voucherRoot);
                root.render(
                    <PaymentVoucher
                        reservation={reservation}
                        client={client}
                        vehicle={vehicle}
                        formatCurrency={formatCurrency}
                    />
                );

                // Wait a bit for React to render before printing
                setTimeout(() => {
                    printWindow.focus();
                    printWindow.print();
                    // Don't close immediately - let user see the print dialog
                }, 500);
            }
        }, 100);
    };
    const handlePhotoUpload = async (e) => {
        e.preventDefault();
        if (newPhotos.length === 0) return;

        const formData = new FormData();
        newPhotos.forEach(photo => {
            formData.append('photos', photo);
        });

        try {
            const res = await axios.post(
                `${API_BASE_URL}/api/reservations/${id}/photos`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );
            setPhotos([...photos, ...res.data]);
            setNewPhotos([]);
        } catch (err) {
            setError('...');
        }
    };

    const handleDeletePhoto = async (photoId) => {
        try {
            await axios.delete(`${API_BASE_URL}/api/reservations/photos/${photoId}`);
            setPhotos(photos.filter(photo => photo.id !== photoId));
        } catch (err) {
            setError(t('delete_photo_error'));
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat(i18nInstance.language === 'ar' ? 'ar-DZ' : 'fr-DZ', {
            style: 'currency',
            currency: 'DZD'
        }).format(amount).replace('DZD', t('currency')).replace('د.ج.‏', t('currency'));
    };

    const handleComplete = async () => {
        try {
            const now = new Date().toISOString();
            const res = await axios.put(
                `${API_BASE_URL}/api/reservations/${id}`,
                {
                    status: 'done',
                    date_retour_effective: now,
                    km_retour: vehicle?.kilometrage || reservation.km_depart
                }
            );
            setReservation(res.data);
        } catch (err) {
            setError(t('complete_error'));
        }
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
        <html>
            <head>
                <title>Reservation #${reservation.id}</title>
                <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
                <style>
                    @media print {
                        body { -webkit-print-color-adjust: exact; }
                        .no-print { display: none !important; }
                    }
                </style>
            </head>
            <body class="bg-white">
                <div id="print-root"></div>
            </body>
        </html>
    `);

        const printRoot = printWindow.document.getElementById('print-root');
        const root = createRoot(printRoot);

        root.render(
            <ReservationPrint
                reservation={reservation}
                client={client}
                vehicle={vehicle}
            />
        );
    };

    const handlePrintFacture = (withTVA = false) => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
        <html>
            <head>
                <title>Facture #${reservation.id}</title>
                <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
                <style>
                    @media print {
                        body { -webkit-print-color-adjust: exact; }
                        .no-print { display: none !important; }
                    }
                </style>
            </head>
            <body class="bg-white">
                <div id="facture-root"></div>
            </body>
        </html>
    `);

        const factureRoot = printWindow.document.getElementById('facture-root');
        const root = createRoot(factureRoot);

        root.render(
            <FacturePrint
                reservation={reservation}
                client={client}
                vehicle={vehicle}
                withTVA={withTVA}
                onClose={() => printWindow.close()}
            />
        );
    };
    if (loading) return (
        <div className="flex items-center justify-center h-screen">
            <div className="text-center">
                <svg className="animate-spin h-12 w-12 text-blue-500 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="mt-4 text-lg font-medium text-gray-600">{t('loading_details')}</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="flex items-center justify-center h-screen">
            <div className="max-w-md p-6 bg-white rounded-xl shadow-md">
                <div className="flex flex-col items-center text-center">

                    <h3 className="mt-4 text-xl font-medium text-gray-900"></h3>
                    <p className="mt-2 text-gray-600">{error}</p>
                    <button
                        onClick={() => navigate(-1)}
                        className={`mt-6 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}
                    >
                        <ArrowLeft className={`w-5 h-5 ${isRtl ? 'ml-2 rotate-180' : 'mr-2'}`} />
                        {t('back')}
                    </button>
                </div>
            </div>
        </div>
    );

    if (!reservation) return (
        <div className="flex items-center justify-center h-screen">
            <div className="max-w-md p-6 bg-white rounded-xl shadow-md">
                <div className="flex flex-col items-center text-center">
                    <svg className="h-16 w-16 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <h3 className="mt-4 text-xl font-medium text-gray-900">{t('reservation_not_found')}</h3>
                    <p className="mt-2 text-gray-600">{t('try_different_filters')}</p>
                    <button
                        onClick={() => navigate('/reservations')}
                        className="mt-6 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        {t('view_all')}
                    </button>
                </div>
            </div>
        </div>
    );

    const statusColors = {
        'en_attente': { bg: 'bg-amber-100', text: 'text-amber-800', icon: <Clock className="w-4 h-4 mr-1" /> },
        'en_cours': { bg: 'bg-blue-100', text: 'text-blue-800', icon: <Car className="w-4 h-4 mr-1" /> },
        'done': { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle className="w-4 h-4 mr-1" /> }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header with decorative SVG */}
            <div className="relative bg-gradient-to-r from-blue-600 to-blue-800 pt-8 pb-16">
                <div className="absolute inset-0 overflow-hidden">
                    <svg className="absolute bottom-0 left-0 opacity-20" width="100%" height="300px" viewBox="0 0 1200 120" preserveAspectRatio="none">
                        <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" fill="#fff"></path>
                        <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" fill="#fff" opacity="0.5"></path>
                    </svg>
                </div>
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className={`flex items-center text-white hover:text-blue-200 transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
                        >
                            <ArrowLeft className={`w-5 h-5 ${isRtl ? 'ml-2 rotate-180' : 'mr-2'}`} />
                            <span className="font-medium">{t('back')}</span>
                        </button>
                        <div className={`flex flex-wrap gap-2 ${isRtl ? 'space-x-reverse' : ''}`}>
                            <button
                                onClick={() => navigate(`/reservations/${id}/edit`)}
                                className={`flex items-center px-4 py-2 bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm border border-white border-opacity-20 rounded-md text-sm font-medium text-white hover:bg-opacity-20 transition-all ${isRtl ? 'flex-row-reverse' : ''}`}
                            >
                                <Edit className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                {t('edit')}
                            </button>
                            <button
                                onClick={handlePrint}
                                className={`flex items-center px-4 py-2 bg-white text-blue-600 rounded-md shadow-sm hover:bg-gray-50 transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
                            >
                                <Printer className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                {t('print')}
                            </button>
                            <button
                                onClick={() => handlePrintFacture(false)}
                                className={`flex items-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
                            >
                                <Printer className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                Facture
                            </button>
                            <button
                                onClick={() => handlePrintFacture(true)}
                                className={`flex items-center px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
                            >
                                <Printer className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                Facture Final
                            </button>
                            <button
                                onClick={handlePrintVoucher}
                                className={`flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
                            >
                                <Printer className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                {t('print_voucher')}
                            </button>
                        </div>
                        {showVoucher && (
                            <div className="mb-8">
                                <PaymentVoucher
                                    reservation={reservation}
                                    client={client}
                                    vehicle={vehicle}
                                    formatCurrency={formatCurrency}
                                />
                            </div>
                        )}
                    </div>
                    <div className="mt-8">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-end">
                            <div>
                                <h1 className={`text-3xl font-bold text-white ${isRtl ? 'text-right' : ''}`}>
                                    {t('reservation_number')} {reservation.id}
                                </h1>
                                <div
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mt-3 ${statusColors[reservation.status].bg
                                        } ${statusColors[reservation.status].text} ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    {statusColors[reservation.status].icon}
                                    {t(reservation.status === 'done' ? 'completed' : reservation.status === 'en_cours' ? 'rental' : 'pending')}
                                </div>
                            </div>
                            <div className={`mt-4 md:mt-0 bg-white bg-opacity-10 backdrop-filter backdrop-blur-sm rounded-lg p-3 ${isRtl ? 'text-right' : ''}`}>
                                <p className="text-xs text-white text-opacity-80">{t('created_on')}</p>
                                <p className="font-medium text-white">
                                    {moment(reservation.created_at).locale(i18nInstance.language).format('DD MMM YYYY, hh:mm A')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main content */}
            <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12`} dir={isRtl ? 'rtl' : 'ltr'}>
                {showVoucher && (
                    <div className="mb-8">
                        <PaymentVoucher
                            reservation={reservation}
                            client={client}
                            vehicle={vehicle}
                            formatCurrency={formatCurrency}
                        />
                        <div className={`mt-4 flex ${isRtl ? 'justify-start' : 'justify-end'}`}>
                            <button
                                onClick={handlePrintVoucher}
                                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors ${isRtl ? 'flex-row-reverse' : ''}`}
                            >
                                <Printer className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                {t('print_voucher')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Summary cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    {/* Client card */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-5">
                            <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <div className="p-3 rounded-full bg-blue-100 text-blue-600">
                                    <User className="w-5 h-5" />
                                </div>
                                <h3 className={`${isRtl ? 'mr-3' : 'ml-3'} font-semibold text-gray-800`}>{t('clients')}</h3>
                            </div>
                        </div>
                        <div className={`p-5 ${isRtl ? 'text-right' : ''}`}>
                            {client ? (
                                <div>
                                    <h4 className="text-lg font-medium text-gray-900">{client.nom} {client.prenom}</h4>
                                    <div className="mt-3 space-y-2">
                                        <div className={`flex items-center text-sm text-gray-600 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <svg className={`w-4 h-4 text-gray-400 ${isRtl ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                            </svg>
                                            {client.telephone}
                                        </div>
                                        {client.email && (
                                            <div className={`flex items-center text-sm text-gray-600 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                                <svg className={`w-4 h-4 text-gray-400 ${isRtl ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                {client.email}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => navigate(`/clients`)}
                                        className={`mt-4 text-sm text-blue-600 hover:text-blue-800 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}
                                    >
                                        {t('view_client_details')}
                                        <svg className={`w-4 h-4 ${isRtl ? 'mr-1 rotate-180' : 'ml-1'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            ) : (
                                <p className="text-gray-500">{t('no_client_info')}</p>
                            )}
                        </div>
                    </div>

                    {/* Vehicle card */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                        <div className="bg-gradient-to-r from-green-50 to-green-100 p-5">
                            <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <div className="p-3 rounded-full bg-green-100 text-green-600">
                                    <Car className="w-5 h-5" />
                                </div>
                                <h3 className={`${isRtl ? 'mr-3' : 'ml-3'} font-semibold text-gray-800`}>{t('vehicles')}</h3>
                            </div>
                        </div>
                        <div className={`p-5 ${isRtl ? 'text-right' : ''}`}>
                            {vehicle ? (
                                <div>
                                    <h4 className="text-lg font-medium text-gray-900">{vehicle.marque} {vehicle.modele}</h4>
                                    <div className="mt-3 space-y-2">
                                        <div className={`flex items-center text-sm text-gray-600 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <svg className={`w-4 h-4 text-gray-400 ${isRtl ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                            {vehicle.matricule}
                                        </div>
                                        <div className={`flex items-center text-sm text-gray-600 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <Gauge className={`w-4 h-4 text-gray-400 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                            {vehicle.kilometrage} km
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => navigate(`/voitures`)}
                                        className={`mt-4 text-sm text-blue-600 hover:text-blue-800 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}
                                    >
                                        {t('view_vehicle_details')}
                                        <svg className={`w-4 h-4 ${isRtl ? 'mr-1 rotate-180' : 'ml-1'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                                        </svg>
                                    </button>
                                </div>
                            ) : (
                                <p className="text-gray-500">{t('no_vehicle_info')}</p>
                            )}
                        </div>
                    </div>

                    {/* Payment card */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                        <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-5">
                            <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <div className="p-3 rounded-full bg-purple-100 text-purple-600">
                                    <DollarSign className="w-5 h-5" />
                                </div>
                                <h3 className={`${isRtl ? 'mr-3' : 'ml-3'} font-semibold text-gray-800`}>{t('finance')}</h3>
                            </div>
                        </div>
                        <div className={`p-5 ${isRtl ? 'text-right' : ''}`}>
                            <div className={`flex justify-between items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <span className="text-sm text-gray-600">{t('total_amount')}</span>
                                <span className="font-medium text-gray-900">{formatCurrency(reservation.total)}</span>
                            </div>

                            <div className={`mt-2 flex justify-between items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <span className="text-sm text-gray-600">{t('deposit')}</span>
                                <span className={`font-medium flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    {formatCurrency(reservation.montant_daman)}
                                    {reservation.daman_rendu ? (
                                        <span className={`${isRtl ? 'mr-2' : 'ml-2'} text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center`}>
                                            <CheckCircle className="w-3 h-3 mr-1" />
                                        </span>
                                    ) : (
                                        <span className={`${isRtl ? 'mr-2' : 'ml-2'} text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full`}></span>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                {/* Detailed sections */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    {/* Rental Period */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                        <div className={`border-b border-gray-200 p-5 ${isRtl ? 'text-right' : ''}`}>
                            <h3 className={`flex items-center text-lg font-semibold text-gray-800 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <Calendar className={`w-5 h-5 ${isRtl ? 'ml-2' : 'mr-2'} text-blue-500`} />
                                {t('rental_period')}
                            </h3>
                        </div>
                        <div className="p-5">
                            <div className="space-y-4">
                                <div className={`flex ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                    <div className="flex-shrink-0">
                                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-100 text-blue-600">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className={isRtl ? 'mr-4' : 'ml-4'}>
                                        <h4 className="text-sm font-medium text-gray-900">{t('start_date')}</h4>
                                        <p className="mt-1 text-sm text-gray-600">
                                            {moment(reservation.date_depart).locale(i18nInstance.language).format('DD MMM YYYY, hh:mm A')}
                                        </p>
                                        {reservation.lieu_depart && (
                                            <div className={`mt-1 flex items-center text-xs text-gray-500 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                                <MapPin className={`w-3 h-3 ${isRtl ? 'ml-1' : 'mr-1'}`} />
                                                {reservation.lieu_depart}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className={`flex ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                    <div className="flex-shrink-0">
                                        <div className="flex items-center justify-center h-10 w-10 rounded-full bg-purple-100 text-purple-600">
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <div className={isRtl ? 'mr-4' : 'ml-4'}>
                                        <h4 className="text-sm font-medium text-gray-900">{t('planned_return')}</h4>
                                        <p className="mt-1 text-sm text-gray-600">
                                            {moment(reservation.date_retour_prevue).locale(i18nInstance.language).format('DD MMM YYYY, hh:mm A')}
                                        </p>
                                        {reservation.lieu_retour && (
                                            <div className={`mt-1 flex items-center text-xs text-gray-500 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                                <MapPin className={`w-3 h-3 ${isRtl ? 'ml-1' : 'mr-1'}`} />
                                                {reservation.lieu_retour}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {reservation.date_retour_effective && (
                                    <div className={`flex ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                        <div className="flex-shrink-0">
                                            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-green-100 text-green-600">
                                                <CheckCircle className="w-5 h-5" />
                                            </div>
                                        </div>
                                        <div className={isRtl ? 'mr-4' : 'ml-4'}>
                                            <h4 className="text-sm font-medium text-gray-900">{t('actual_return')}</h4>
                                            <p className="mt-1 text-sm text-gray-600">
                                                {moment(reservation.date_retour_effective).locale(i18nInstance.language).format('DD MMM YYYY, hh:mm A')}
                                            </p>
                                        </div>
                                    </div>
                                )}

                                <div className="pt-4 border-t border-gray-200">
                                    <div className={`flex justify-between ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <span className="text-sm font-medium text-gray-500">{t('duration')}</span>
                                        <span className="text-sm font-medium text-gray-900">
                                            {moment(reservation.date_retour_prevue).diff(moment(reservation.date_depart), 'days')} {t('days')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Kilometer Details */}
                    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                        <div className={`border-b border-gray-200 p-5 ${isRtl ? 'text-right' : ''}`}>
                            <h3 className={`flex items-center text-lg font-semibold text-gray-800 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <Gauge className={`w-5 h-5 text-green-500 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                {t('km_details')}
                            </h3>
                        </div>
                        <div className="p-5">
                            <div className={`space-y-4 ${isRtl ? 'text-right' : ''}`}>
                                <div className={`flex justify-between items-center p-3 bg-gray-50 rounded-lg ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <div className={`p-2 rounded-full bg-blue-100 text-blue-600 ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                                            </svg>
                                        </div>
                                        <div className={isRtl ? 'text-right' : 'text-left'}>
                                            <h4 className="text-sm font-medium text-gray-900">{t('km_departure_label')}</h4>
                                            <p className="text-xs text-gray-500">{t('at_departure')}</p>
                                        </div>
                                    </div>
                                    <span className="font-mono font-medium">{reservation.km_depart} km</span>
                                </div>

                                <div className={`flex justify-between items-center p-3 bg-gray-50 rounded-lg ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <div className={`p-2 rounded-full bg-purple-100 text-purple-600 ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                                            </svg>
                                        </div>
                                        <div className={isRtl ? 'text-right' : 'text-left'}>
                                            <h4 className="text-sm font-medium text-gray-900">{t('return_mileage_label')}</h4>
                                            <p className="text-xs text-gray-500">{t('at_return')}</p>
                                        </div>
                                    </div>
                                    <span className="font-mono font-medium">
                                        {reservation.km_retour ? `${reservation.km_retour} km` : '-'}
                                    </span>
                                </div>

                                {reservation.km_retour && (
                                    <div className="pt-4 border-t border-gray-200">
                                        <div className={`flex justify-between ${isRtl ? 'flex-row-reverse' : ''}`}>
                                            <span className="text-sm font-medium text-gray-500">{t('distance_traveled')}</span>
                                            <span className="text-sm font-medium text-blue-600">
                                                {reservation.km_retour - reservation.km_depart} km
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Financial Details */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 mb-8">
                    <div className={`border-b border-gray-200 p-5 ${isRtl ? 'text-right' : ''}`}>
                        <h3 className={`flex items-center text-lg font-semibold text-gray-800 ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <DollarSign className={`w-5 h-5 text-amber-500 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                            {t('finance')}
                        </h3>
                    </div>
                    <div className="p-5">
                        <div className="space-y-4">
                            <div className={`flex justify-between ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <div className={`p-2 rounded-full bg-gray-100 text-gray-600 ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">{t('base_price_day')}</span>
                                </div>
                                <span className="text-sm font-medium">{formatCurrency(reservation.prix_base)}</span>
                            </div>
                            <div className={`flex justify-between pt-3 border-t border-gray-200 ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <div className={`p-2 rounded-full bg-amber-100 text-amber-600 ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm font-medium text-gray-900">{t('payment_status')}</span>
                                </div>
                                <span className="text-sm font-medium capitalize">
                                    {t(reservation.mode_paiement)}
                                </span>
                            </div>

                            {reservation.versement > 0 && (
                                <div className={`flex justify-between ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                    <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <div className={`p-2 rounded-full bg-green-100 text-green-600 ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                                    d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm font-medium text-gray-900">{t('initial_versement')}</span>
                                    </div>
                                    <span
                                        className="text-sm font-medium">{formatCurrency(reservation.versement)}</span>
                                </div>
                            )}

                            {reservation.remise > 0 && (
                                <div className={`flex justify-between ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                    <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <div className={`p-2 rounded-full bg-green-100 text-green-600 ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                                            </svg>
                                        </div>
                                        <span className="text-sm font-medium text-gray-900">{t('discount')}</span>
                                    </div>
                                    <span
                                        className="text-sm font-medium text-red-500">-{formatCurrency(reservation.remise)}</span>
                                </div>
                            )}
                            {reservation.remise_description && (
                                <div className="mt-4 p-4 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 shadow-sm">
                                    <div className={`flex items-start ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                        <div className={`flex-shrink-0 p-2 bg-blue-100 rounded-lg text-blue-600 ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                                            </svg>
                                        </div>
                                        <div className={isRtl ? 'mr-3' : 'ml-3'}>
                                            <h4 className="text-sm font-semibold text-blue-800 flex items-center">
                                                {t('description')}
                                            </h4>
                                            <p className="mt-1 text-sm text-blue-700">
                                                {reservation.remise_description}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {reservation.retard_prix > 0 && (
                                <div className={`flex justify-between ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                    <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <div className={`p-2 rounded-full bg-red-100 text-red-600 ${isRtl ? 'ml-3' : 'mr-3'}`}>
                                            <AlertCircle className="w-4 h-4" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-900">{t('late_penalty')}</span>
                                    </div>
                                    <span
                                        className="text-sm font-medium text-red-500">+{formatCurrency(reservation.retard_prix)}</span>
                                </div>
                            )}

                            <div className="pt-4 border-t border-gray-200">
                                <div className={`flex justify-between ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                                    <span className="font-medium text-gray-900">{t('total_amount')}</span>
                                    <span
                                        className="font-medium text-blue-600">{formatCurrency(reservation.total)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Vehicle Photos */}
                <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 mb-8">
                    <div className={`border-b border-gray-200 p-5 ${isRtl ? 'text-right' : ''}`}>
                        <h3 className={`flex items-center text-lg font-semibold text-gray-800 ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <Camera className={`w-5 h-5 ${isRtl ? 'ml-2' : 'mr-2'} text-indigo-500`} />
                            {t('vehicle_photos')}
                        </h3>
                    </div>
                    <div className="p-5">
                        {photos.length > 0 ? (
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                                {photos.map(photo => (
                                    <div key={photo.id} className="relative group">
                                        <div className="aspect-w-4 aspect-h-3 bg-gray-100 rounded-lg overflow-hidden">
                                            <img
                                                src={`${API_BASE_URL}${photo.photo_url}`}
                                                alt={`Reservation ${reservation.id}`}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <button
                                            onClick={() => handleDeletePhoto(photo.id)}
                                            className={`absolute top-2 ${isRtl ? 'left-2' : 'right-2'} bg-white bg-opacity-80 rounded-full p-1.5 shadow-sm hover:bg-red-500 hover:text-white transition-colors`}
                                            title={t('delete_photo')}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <div className="mx-auto h-24 w-24 text-gray-400">
                                    <Camera className="w-full h-full" />
                                </div>
                                <h4 className="mt-3 text-sm font-medium text-gray-900">{t('no_photos')}</h4>
                                <p className="mt-1 text-sm text-gray-500">{t('upload_condition_desc')}</p>
                            </div>
                        )}

                        <form onSubmit={handlePhotoUpload} className="mt-6">
                            <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <label className="flex-1">
                                    <span className="sr-only">{t('choose_photos')}</span>
                                    <input
                                        type="file"
                                        multiple
                                        onChange={(e) => setNewPhotos(Array.from(e.target.files))}
                                        className={`block w-full text-sm text-gray-500
                                        ${isRtl ? 'file:ml-4' : 'file:mr-4'} file:py-2 file:px-4
                                        file:rounded-md file:border-0
                                        file:text-sm file:font-semibold
                                        file:bg-blue-50 file:text-blue-700
                                        hover:file:bg-blue-100`}
                                    />
                                </label>
                                <button
                                    type="submit"
                                    disabled={newPhotos.length === 0}
                                    className={`${isRtl ? 'mr-3' : 'ml-3'} px-4 py-2 rounded-md text-sm font-medium ${newPhotos.length > 0 ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'}`}
                                >
                                    {t('upload')}
                                </button>
                            </div>
                            <p className={`mt-2 text-xs text-gray-500 ${isRtl ? 'text-right' : ''}`}>{t('upload_format_info')}</p>
                        </form>
                    </div>
                </div>

                {/* Mark as returned button */}
                {reservation.status === 'en_cours' && (
                    <div className="text-center mb-12">
                        <button
                            onClick={handleComplete}
                            className="inline-flex items-center px-8 py-4 bg-green-600 text-white text-lg font-bold rounded-xl shadow-lg hover:bg-green-700 transform hover:scale-105 transition-all"
                        >
                            <CheckCircle className={`w-6 h-6 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                            {t('mark_as_returned')}
                        </button>
                    </div>
                )}
            </div>
        </div >
    );
};

export default ReservationDetail;